// Generic pure-TS store for a *keyed snapshot* (ADR 011 §2c) — the shared engine
// behind `lot-state-store.ts` (ADR 042 §§1–4, BL-1251) and
// `registration-state-store.ts` (BL-1262).
//
// THE PATTERN. An entity-state stream broadcasts ONE changed entity's absolute
// current state per mutation on an auction-wide topic, and the client folds those
// deltas into a `Map<entityId, TDelta>` keyed on a per-entity cursor
// (last-writer-wins — highest cursor per entity). Because every delta carries
// ABSOLUTE values (not increments), the fold is:
//   • idempotent      — duplicate delivery is a no-op
//   • order-tolerant  — out-of-order resolves to the max cursor
//   • gap-healing ONLY on a sequenced stream (BL-1628) — LWW by itself heals
//                       DELIVERY (duplicate, out-of-order, late): a re-delivered
//                       delta stops mattering the moment any later delta for that
//                       entity arrives, since it carries the whole truth. It does
//                       NOTHING for a SKIPPED delta, which is a different failure.
//                       So it depends on what the stream's position is:
//
//                       • Position is a dense per-topic SEQUENCE (the payload
//                         carries `sequence` — ALL THREE admin streams as of
//                         BL-1630): the engine detects the hole on the next delta
//                         and fetches exactly what was missed. See `detectGap` below.
//                       • Position is a transaction-START timestamp (`cursor`): NOT
//                         gap-healing. A slow-committing row lands BELOW the client's
//                         cursor and catch-up (`>= cursor`) never returns it — stale
//                         until that entity next changes, which on a quiet one may be
//                         never. NO stream is on this shape any more; the branch stays
//                         because the engine is generic and a future binding could
//                         arrive unsequenced.
//
//                       Corrected BL-1627 (this bullet used to read
//                       "self-healing"), then half-closed by BL-1628. A stream
//                       turns detection on by claiming a sequence server-side and
//                       naming its position `sequence`; there is nothing to build
//                       here. ADR 011 §2c / ADR 042 §1.
// The server's base table is the source of truth and catch-up re-reads it, so a
// cold seed always recovers; nothing is ever corrupted, only stale.
//
// Unlike SnapshotStore (one snapshot per topic, per-topic monotonic version),
// this holds a PER-ENTITY map with a PER-ENTITY cursor on ONE auction-wide topic.
// The topic itself is learned from the authenticated catch-up response (the
// obscured admin channel is minted server-side), so the store subscribes only
// AFTER the first cold-seed returns the topic.
//
// WHY GENERIC (sharing.md § 4 reuse-first). BL-1262 needed the identical engine
// for registrations. The two streams differ in exactly four places — the entity's
// id field, its cursor field, the broadcast event name, and the catch-up
// response's collection key — all of which are now config. Everything that
// actually carries risk (the strict cursor guard, the copy-on-write map, the
// pre-copy dedupe, sticky `hasSeeded`, cold-seed-failure retry via reconcile) is
// written ONCE here. Forking a second 150-line store would have meant two places
// for a fold bug to hide, and only one of them would get fixed.

import type { RealtimeEventName } from '../../types/realtime/events';
import type { RealtimeSubscriber } from '../../types/realtime/subscriber';
import type { Unsubscribe } from '../../types/realtime/types';
import { formatRealtimeFailure } from '../../isomorphic/realtime/realtime-failure-log';
import { DepartureTracker, type DeparturesConfig, type MembershipConfig } from './entity-departures';

/**
 * Shape every entity-state catch-up response satisfies: the minted obscured
 * `topic`, the changed entities, and the max `cursor` in that set (the client's
 * next `since_cursor`). The entity collection's KEY differs per stream (`lots`,
 * `registrations`), so it's read via `itemsOf` rather than named here.
 */
export interface EntityStateCatchupResultBase {
    topic: string;
    /**
     * The client's next position, for a stream whose ordering key is a wall-clock
     * timestamp (`updated_at` as epoch ms). Mutually exclusive with `sequence`.
     */
    cursor?: number;
    /**
     * The client's next position, for a stream whose ordering key is a dense
     * per-topic SEQUENCE claimed in commit order (BL-1628).
     *
     * Carrying it under its own name is what turns gap detection on: a timestamp
     * cursor is sparse and unordered-by-commit, so `N+1` means nothing there, while
     * a sequence is dense and a hole in it is real. Streams migrate one at a time by
     * renaming this field server-side — no client config changes (AC 9).
     */
    sequence?: number;
}

/**
 * Per-stream configuration — the only five things that differ between the lot,
 * registration and max-bid streams.
 *
 * Gap detection is deliberately NOT one of them (BL-1628 AC 9). Whether a stream
 * can detect a hole is a property of its DATA — its position is either a dense
 * per-topic sequence or a sparse wall-clock cursor — so the engine learns it from
 * the catch-up response (`result.sequence`) and then reads each frame's position
 * through the `cursorOf` the binding already supplies. A binding that had to
 * declare a `detectsGaps` flag could forget to, and would silently lose gap
 * recovery; data cannot forget what shape it is.
 */
export interface EntityStateStoreConfig<TDelta, TResult extends EntityStateCatchupResultBase> {
    /** The delta's fold key (`lotId`, `registrationId`). */
    keyOf: (delta: TDelta) => string;
    /** The delta's LWW ordering cursor (epoch ms). */
    cursorOf: (delta: TDelta) => number;
    /** Broadcast event name on the obscured admin topic. */
    event: RealtimeEventName;
    /** Pull the entity collection out of the catch-up response. */
    itemsOf: (result: TResult) => readonly TDelta[];
    /**
     * Pull the deltas out of ONE broadcast frame (BUG-1517).
     *
     * Streams differ in frame arity: the lot stream batches N changed lots into
     * one `lot_state.delta` (its topic is per-auction, so N frames collapse to
     * one), while the registration and max-bid streams still send one delta per
     * frame. The engine folds whatever this returns, so a stream that batches
     * later only changes its own binding — not this file, and not its siblings.
     */
    deltasOf: (payload: unknown) => readonly TDelta[];
    /**
     * Which deltas this store KEEPS. Absent = keep everything (every stream but
     * the lot-scoped lot-state binding, BL-1542 AC 35).
     *
     * THIS IS A FOLD FILTER, NOT A DELIVERY FILTER, AND THE DIFFERENCE IS THE
     * WHOLE POINT. A scoped store still subscribes to a topic that carries other
     * entities — the admin lot-state topic is per-AUCTION — so its cursor and its
     * gap detection MUST still observe every delta on the wire. Dropping foreign
     * deltas before the fold would freeze `maxCursor` behind them, and the very
     * next delta for the entity we DO care about would read as a hole: reconcile
     * fires, returns nothing new (the server-side read is scoped too), the cursor
     * stays put, and every foreign delta triggers another round. A reconcile storm
     * that gets worse the busier the sale is.
     *
     * So the predicate is applied INSIDE the fold loop, after the cursor has
     * advanced and after `detectGap` has seen the frame — see `foldMany`.
     */
    retains?: (delta: TDelta) => boolean;
    /** BUG-1766 — entities leaving the topic; absent = none. See `entity-departures.ts`. */
    departures?: DeparturesConfig;
    /** BUG-1766 — the catch-up's live-entity count check; absent = none. */
    membership?: MembershipConfig<TDelta, TResult>;
}

/**
 * Apply one delta to the fold map with last-writer-wins per entity. Mutates
 * `map`. Returns `true` iff the map changed (the delta was newer than what we
 * held).
 *
 * The guard is `> existing cursor` (strict): an equal-or-lower cursor is a
 * duplicate / stale / out-of-order delivery and is dropped. This is what makes
 * the fold idempotent and order-independent.
 */
export function applyDeltaToEntityMap<TDelta>(
    map: Map<string, TDelta>,
    delta: TDelta,
    keyOf: (d: TDelta) => string,
    cursorOf: (d: TDelta) => number,
): boolean {
    const key = keyOf(delta);
    const existing = map.get(key);
    if (existing !== undefined && cursorOf(delta) <= cursorOf(existing)) return false;
    map.set(key, delta);
    return true;
}

/**
 * Fold a batch of deltas into a fresh map with last-writer-wins per entity.
 * Pure — the result is identical regardless of input order or duplicates.
 */
export function foldEntityDeltas<TDelta>(
    deltas: readonly TDelta[],
    keyOf: (d: TDelta) => string,
    cursorOf: (d: TDelta) => number,
): Map<string, TDelta> {
    const map = new Map<string, TDelta>();
    for (const delta of deltas) applyDeltaToEntityMap(map, delta, keyOf, cursorOf);
    return map;
}

/**
 * How a coalesced render notification is scheduled (BUG-1407).
 *
 * Takes the flush callback, returns a cancel fn. Injected so tests can pump it
 * by hand — a test that waited on a real frame would be slow and flaky, and the
 * assertions this seam supports ("exactly one render for this burst") need the
 * flush point to be exact, not approximate.
 */
export type NotifyScheduler = (flush: () => void) => () => void;

/**
 * One frame's worth of time. The window only has to span the gap between
 * broadcasts belonging to ONE server transaction (the post-commit publish queue
 * drains them back-to-back over a single connection), and it must stay short
 * enough to be invisible on a live-bidding surface.
 */
const NOTIFY_COALESCE_MS = 16;

/**
 * Default: a short macrotask window.
 *
 * `setTimeout`, deliberately NOT `queueMicrotask`. Live deltas arrive as
 * separate transport messages, i.e. separate TASKS — a microtask hop drains
 * before the next message is even read, so it would coalesce nothing and the
 * bug would survive with a green test. (`batching-catchup-fetcher.ts` documents
 * the mirror image of this: its burst is issued inside ONE task, so a microtask
 * is exactly right there and a macrotask would break it. Same window, opposite
 * conclusion, because the input arrives differently.)
 *
 * Also deliberately not `requestAnimationFrame`: rAF does not fire in a hidden
 * tab, so a notify scheduled just before the operator switched away would sit
 * unflushed until they came back. The fold is already applied by then, so this
 * is a rendering delay rather than a correctness bug — but a timer has no such
 * cliff and needs no environment branching for SSR / node.
 */
const defaultNotifyScheduler: NotifyScheduler = (flush) => {
    const id = setTimeout(flush, NOTIFY_COALESCE_MS);
    return () => clearTimeout(id);
};

/**
 * Whether the store has established its stream. Consumers render a connection
 * banner from this — an entity COUNT can't stand in for it, since a stream may
 * legitimately be empty (an auction with zero lots, or zero registrations).
 */
export interface EntityStateConnection {
    /** A catch-up has succeeded and the transport subscription is open. */
    isSubscribed: boolean;
    /** A cold-seed has succeeded at least once — sticky across a later drop. */
    hasSeeded: boolean;
    /**
     * The last catch-up failure, or `null` when the most recent attempt
     * succeeded (BL-1552). NOT sticky — cleared on the next success, so a
     * consumer never strands an operator on a banner for a feed that recovered.
     *
     * Load-bearing for telling "this scope is empty" apart from "I could not
     * read this scope". An entity COUNT cannot answer that: zero rows is both
     * the correct answer for a quiet lot and the symptom of a failed read, and
     * rendering the second as the first is BUG-1467's failure mode — a
     * confident, legitimate-looking empty state that is simply wrong.
     */
    lastError: string | null;
}

/**
 * Normalize a thrown value to a message for {@link EntityStateConnection.lastError}.
 * Never the raw error object — this crosses into React state and is rendered.
 */
function errorMessageOf(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

/** Stable pre-connection value (ref-stable for `useSyncExternalStore`). */
export const DISCONNECTED_ENTITY_STATE: EntityStateConnection = {
    isSubscribed: false,
    hasSeeded: false,
    lastError: null,
};

/**
 * A keyed-snapshot entity store for ONE auction-wide topic. Owns the transport
 * subscription, the LWW-per-entity fold, the cursor, and cold-seed / reconcile
 * catch-up. An owner constructs one per SCOPE — an auction id for the
 * registrations stream, a LOT id for the per-lot max-bid stream (BL-1552) — and
 * drives `start()` /
 * `stop()` / `reconcile()`; selectors read via `subscribe()` + `getEntities()`
 * (the `useSyncExternalStore` contract) and `getConnection()`.
 */
export class EntityStateStore<TDelta, TResult extends EntityStateCatchupResultBase> {
    /**
     * The PUBLISHED fold — what `getEntities()` returns. Replaced only at a
     * flush, so it is stable for every reader between notifications (BUG-1407).
     */
    private entities = new Map<string, TDelta>();
    /**
     * The UNPUBLISHED fold: deltas landed since the last flush, or `null` when
     * nothing is waiting. Readers never see this — that is the whole point.
     */
    private pending: Map<string, TDelta> | null = null;
    private maxCursor = 0;
    /**
     * Whether this stream's position is a dense per-topic sequence, learned from the
     * first catch-up that carries one (BL-1628). `false` keeps the pre-BL-1628
     * behaviour exactly.
     *
     * As of BL-1630 every production binding is sequenced — max bids left the
     * unsequenced set in BL-1629 and registrations in BL-1630, each by renaming its
     * position field and nothing else. The `false` branch is retained because the
     * engine is generic, not lot-shaped (BL-1628 AC 9): a future binding may arrive
     * before its server side is sequenced, and it must degrade rather than break.
     * `entity-state-store.gap-detection.test.ts` still drives a synthetic unsequenced
     * stream to keep that branch honest.
     */
    private isSequenced = false;
    /** Departure frames, stale-delta guard and membership reseed (BUG-1766). */
    private readonly departures: DepartureTracker<TDelta, TResult>;
    /** An in-flight gap-triggered reconcile, so a burst of gapped deltas fires one. */
    private gapReconcileInFlight = false;
    private topic: string | null = null;
    private transportUnsub: Unsubscribe | null = null;
    private readonly listeners = new Set<() => void>();
    /** Cancel handle for a scheduled-but-unflushed notify, or `null`. */
    private pendingNotify: (() => void) | null = null;
    private started = false;
    /** The PUBLISHED connection — what `getConnection()` returns. */
    private connection: EntityStateConnection = DISCONNECTED_ENTITY_STATE;
    /**
     * The UNPUBLISHED connection: staged since the last flush, or `null` when
     * nothing is waiting. Readers never see this — same rule as `pending`, and
     * for the same reason (BUG-1562).
     */
    private pendingConnection: EntityStateConnection | null = null;

    constructor(
        private readonly subscriber: RealtimeSubscriber,
        private readonly fetcher: (scopeId: string, sinceCursor: number) => Promise<TResult>,
        /**
         * What this store is scoped to. Deliberately NOT named `auctionId`: since
         * BL-1552 the max-bid binding passes a LOT id, and a field named for one
         * caller's scope silently mislabels the other's — including in the two
         * failure logs below, where it would report a lot id under `auctionId`.
         */
        private readonly scopeId: string,
        private readonly config: EntityStateStoreConfig<TDelta, TResult>,
        private readonly scheduleNotify: NotifyScheduler = defaultNotifyScheduler,
    ) {
        this.departures = new DepartureTracker<TDelta, TResult>(
            {
                working: () => this.workingEntities(),
                commit: (next) => {
                    this.pending = next;
                    this.notify();
                },
                advance: (position) => {
                    if (position > this.maxCursor) this.maxCursor = position;
                },
                checkGap: (positions) => this.detectGapAt(positions),
                fetchSeed: () => this.fetcher(this.scopeId, 0),
                positionOf: (result) => result.sequence ?? result.cursor ?? 0,
                itemsOf: config.itemsOf,
                keyOf: config.keyOf,
                cursorOf: config.cursorOf,
                retains: config.retains,
            },
            config.membership,
        );
    }

    /**
     * Register a change listener (the `useSyncExternalStore` subscribe seam).
     * Returns an unsubscribe fn. Does NOT itself trigger network work — the
     * provider owns `start()` — so multiple selectors share one fold.
     */
    subscribe(listener: () => void): Unsubscribe {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    /**
     * The current PUBLISHED entity map.
     *
     * Stable between notifications BY CONSTRUCTION (BUG-1407): a delta that has
     * landed but not yet flushed sits in `pending` and is invisible here. That is
     * what `useSyncExternalStore` requires — React caches `getSnapshot` per render
     * and re-reads it on every re-render, including ones this store did not cause,
     * so a value that changes without a paired notify tears. The clerk console tore
     * once a second, because its countdown tick re-rendered on its own schedule and
     * read a half-applied burst.
     */
    getEntities(): ReadonlyMap<string, TDelta> {
        return this.entities;
    }

    /**
     * The newest fold, published or not — the base a new delta folds onto and the
     * map the LWW cursor-guard compares against. Never handed to a reader.
     */
    private workingEntities(): Map<string, TDelta> {
        return this.pending ?? this.entities;
    }

    /**
     * Promote the unpublished fold AND the unpublished connection into the values
     * readers see. No-op for whichever is empty.
     *
     * Both together, at one point, is the whole contract (BUG-1562): a reader
     * pairing `getConnection()` with `getEntities()` must never see a state the
     * server was never in.
     */
    private publishPending(): void {
        if (this.pending !== null) {
            this.entities = this.pending;
            this.pending = null;
        }
        if (this.pendingConnection !== null) {
            this.connection = this.pendingConnection;
            this.pendingConnection = null;
        }
    }

    /**
     * Current PUBLISHED stream state. Stable reference until it actually changes,
     * so it is safe as a `useSyncExternalStore` snapshot.
     *
     * Published at the flush alongside `getEntities()`, for the same reason and by
     * the same mechanism (BUG-1562). It used to be assigned the moment `start()` /
     * `reconcile()` decided it — which, because `notify()` coalesces, meant the
     * seeded connection became readable up to one notify window BEFORE the seeded
     * rows did. Any render inside that window (the clerk console's countdown tick
     * is one) read `hasSeeded: true` beside the pre-seed map, and
     * `useRegistrationRereadTrigger` — whose cold-seed guard latches on exactly
     * that pair — recorded the empty signature as the seed's and then refetched
     * when the real seed landed.
     */
    getConnection(): EntityStateConnection {
        return this.connection;
    }

    /**
     * The newest connection value, published or not — what a further
     * `setConnection` merges onto. Never handed to a reader; the entity-side twin
     * of {@link workingEntities}.
     */
    private workingConnection(): EntityStateConnection {
        return this.pendingConnection ?? this.connection;
    }

    /** Stage the connection value and notify — only when something changed. */
    private setConnection(next: Partial<EntityStateConnection>): void {
        const current = this.workingConnection();
        const merged = { ...current, ...next };
        if (
            merged.isSubscribed === current.isSubscribed &&
            merged.hasSeeded === current.hasSeeded &&
            merged.lastError === current.lastError
        ) {
            return;
        }
        this.pendingConnection = merged;
        this.notify();
    }

    /**
     * Cold-seed: catch up from cursor 0 (full current set), fold, then subscribe
     * to the obscured admin topic returned by the catch-up. Idempotent — safe to
     * call once from the provider's mount effect. Best-effort: a failed cold-seed
     * leaves the map empty and self-heals on the next reconcile.
     */
    async start(): Promise<void> {
        if (this.started) return;
        // BL-2342 (ADR 066 — Offline Mode § 6.2): no cold seed while the
        // transport is suspended for offline mode. `started` stays false, so the
        // first reconcile after the resume seeds and subscribes instead.
        if (this.subscriber.isSuspended?.()) return;
        this.started = true;
        try {
            const result = await this.fetcher(this.scopeId, 0);
            this.topic = result.topic;
            this.ingest(result);
            this.openTransportSubscription(result.topic);
            this.setConnection({ isSubscribed: true, hasSeeded: true, lastError: null });
        } catch (err) {
            // Best-effort — reconcile triggers retry. Reset started so a
            // reconcile still runs; the topic stays null until a catch-up succeeds.
            //
            // BUG-1272 — the retry is silent by design, but the FAILURE must not
            // be. A consumer gate of `staticRows == null || !hasSeeded` renders
            // an identical spinner for "still loading" and "never seeded", so a
            // discarded error here is unobservable at every layer above. Log the
            // cause, exactly as `ledger-store` / `snapshot-store` already do.
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[EntityStateStore] cold seed failed',
                    { scopeId: this.scopeId, event: this.config.event },
                    err,
                ),
            );
            this.started = false;
            this.setConnection({ lastError: errorMessageOf(err) });
        }
    }

    /** Tear down the transport subscription (provider unmount). */
    stop(): void {
        this.transportUnsub?.();
        this.transportUnsub = null;
        this.started = false;
        // `hasSeeded` stays sticky — it records that this store ever reached a
        // good state, which is what distinguishes "still connecting" from
        // "dropped after connecting" for the consumer's banner.
        this.setConnection({ isSubscribed: false });
        // BUG-1407: drop anything still scheduled, INCLUDING the notify the line
        // above may just have queued. `stop()` runs from the provider's unmount
        // cleanup, so there is no longer a consumer to render — the connection
        // VALUE is still updated (a later `getConnection()` reads correctly),
        // only the render is dropped. Cancelling last rather than first is what
        // makes that hold for both sources of a pending notify.
        this.cancelPendingNotify();
    }

    /**
     * Reconcile: catch up every entity changed since `maxCursor` and fold. Wired
     * to the focus / visibility / online / interval / reconnect triggers. If the
     * cold-seed never succeeded (no topic yet), this doubles as the retry that
     * establishes the subscription.
     */
    async reconcile(): Promise<void> {
        // BL-2342: nothing is fetched while suspended for offline mode.
        if (this.subscriber.isSuspended?.()) return;
        try {
            const result = await this.fetcher(this.scopeId, this.maxCursor);
            if (!this.topic) {
                this.topic = result.topic;
                this.started = true;
                this.openTransportSubscription(result.topic);
                this.setConnection({ isSubscribed: true, hasSeeded: true });
            }
            this.ingest(result);
            await this.departures.reconcileMembership(result);
            this.setConnection({ lastError: null });
        } catch (err) {
            // Best-effort — the next trigger retries. Same BUG-1272 reasoning as
            // `start()`: a reconcile that keeps failing is how a store that never
            // seeded stays stuck, so the cause is logged rather than discarded.
            // eslint-disable-next-line no-console
            console.error(
                ...formatRealtimeFailure(
                    '[EntityStateStore] reconcile failed',
                    { scopeId: this.scopeId, event: this.config.event },
                    err,
                ),
            );
            this.setConnection({ lastError: errorMessageOf(err) });
        }
    }

    /** Subscribe to the stream's delta event on the obscured admin topic. */
    private openTransportSubscription(topic: string): void {
        this.transportUnsub?.();
        const unsubDeltas = this.subscriber.subscribe({
            topic,
            event: this.config.event,
            handler: (payload) => {
                // BUG-1517: one frame carries N entries (length 1 for a single
                // mutation). `foldMany` applies the whole frame against ONE
                // working-map copy and notifies once, so a 2000-lot reorder is
                // one render — the same guarantee BUG-1407's coalescing gave a
                // burst of separate frames, now held at the frame level too.
                this.foldMany(this.config.deltasOf(payload));
            },
        });
        const departures = this.config.departures;
        const unsubDepartures = departures
            ? this.subscriber.subscribe({
                  topic,
                  event: departures.event,
                  handler: (payload) => {
                      this.departures.onFrame(departures.entriesOf(payload), departures.removes);
                  },
              })
            : null;
        this.transportUnsub = () => {
            unsubDeltas();
            unsubDepartures?.();
        };
    }

    /** Fold a catch-up batch; advance the cursor; notify once if anything changed. */
    private ingest(result: TResult): void {
        const next = new Map(this.workingEntities());
        let changed = false;
        for (const delta of this.config.itemsOf(result)) {
            // Belt-and-braces for a scoped store: the server-side read is
            // narrowed too (`?lot_id=`), so this normally filters nothing. It
            // matters if a scoped store is ever handed an unscoped fetcher —
            // the fold stays scoped rather than silently widening. The cursor
            // below is taken from the RESULT, not from the kept items, so
            // filtering here cannot stall it.
            if (this.config.retains && !this.config.retains(delta)) continue;
            if (this.departures.isBeforeDeparture(this.config.keyOf(delta), this.config.cursorOf(delta))) continue;
            if (
                applyDeltaToEntityMap(next, delta, this.config.keyOf, this.config.cursorOf)
            ) {
                changed = true;
            }
        }
        // A sequenced stream is identified by the field it uses for its position,
        // not by configuration (BL-1628). Sticky once seen: a later result cannot
        // un-sequence the stream, so a malformed response can't silently disable
        // gap detection for the rest of the session.
        if (result.sequence != null) this.isSequenced = true;
        const position = result.sequence ?? result.cursor ?? 0;
        if (position > this.maxCursor) this.maxCursor = position;
        if (changed) {
            this.pending = next;
            this.notify();
        }
    }

    /**
     * Fold a live frame's deltas (LWW per entity); advance cursor; notify once
     * if anything changed (BUG-1517); and, on a sequenced stream, detect a hole
     * and recover it (BL-1628).
     *
     * One working-map copy for the whole frame rather than one per entry — a
     * 2000-entry frame folded one-at-a-time would copy a 2000-entry map
     * 2000 times.
     */
    private foldMany(deltas: ReadonlyArray<TDelta>): void {
        this.detectGap(deltas);
        const working = this.workingEntities();
        let next: Map<string, TDelta> | null = null;
        for (const delta of deltas) {
            const key = this.config.keyOf(delta);
            const cursor = this.config.cursorOf(delta);
            // FIRST, unconditionally: this store's position on the stream is a
            // fact about the WIRE, not about what it chose to keep. Advancing
            // here rather than after the two `continue`s below is what lets a
            // scoped store ignore a foreign delta without stalling behind it —
            // see `retains`. Behaviour-neutral for an unscoped store: the LWW
            // skip below only fires when `cursor <= cursorOf(existing)`, and
            // `existing` has already been counted, so the advance is a no-op on
            // exactly that path.
            if (cursor > this.maxCursor) this.maxCursor = cursor;
            // Not ours — counted, not kept (BL-1542 AC 35 / RAC 20).
            if (this.config.retains && !this.config.retains(delta)) continue;
            // BUG-1766: a delta from before the entity left the topic must not re-add it.
            if (this.departures.isBeforeDeparture(key, cursor)) continue;
            // Guard against the map we are building, so two deltas for the same
            // entity inside ONE frame still resolve LWW against each other.
            const source = next ?? working;
            const existing = source.get(key);
            if (existing !== undefined && cursor <= this.config.cursorOf(existing)) continue;
            next ??= new Map(working);
            next.set(key, delta);
        }
        if (next === null) return;
        this.pending = next;
        this.notify();
    }

    /**
     * Detect a hole in a sequenced stream and recover exactly what was missed.
     *
     * The mechanic the singleton snapshots have always had (`version == cursor + 1`,
     * `SnapshotContext`), brought to the keyed arity. Three cases, comparing the
     * frame's LOWEST position against the one this client holds:
     *
     *   • `N+1` — the next frame in the series. Apply it; issue nothing (AC 3). On a
     *     healthy socket this is every frame, which is what let the blind 30 s poll
     *     go (AC 6).
     *   • `> N+1` — a hole. Apply the frame AND catch up on `sequence > N`, which
     *     returns every entity missed in between — including one this client has
     *     never seen, which a per-entity cursor could never have discovered (AC 4,
     *     AC 7).
     *   • `<= N` — a duplicate or an out-of-order re-delivery. Nothing to do here;
     *     the LWW guard in the caller drops it (RAC 1).
     *
     * **Why the frame's LOWEST, not its highest** (BUG-1517 made this matter): one
     * frame carries every lot a single finalizer run stamped for one auction, so its
     * sequences are a contiguous run. The run's floor is what must sit adjacent to
     * the held position — comparing the ceiling would read a legitimate 20-lot frame
     * spanning 41..60, arriving at a client holding 40, as a 19-wide hole and fire a
     * pointless full catch-up on every bulk reorder.
     *
     * **Why the position comes from `cursorOf` rather than the envelope.** For a
     * sequenced stream the per-entity ordering key IS the sequence, so the binding's
     * existing accessor already yields it and no sixth config key is needed (AC 9).
     * Whether the stream is sequenced at all is learned once, in `ingest`, from the
     * catch-up response naming its position `sequence` rather than `cursor`.
     *
     * Deliberately does NOT gate on `maxCursor > 0`: a client whose cold seed returned
     * an empty auction sits at 0, and a first frame at 1 is `N+1` — correct with no
     * special case. A first frame at 5 on that same client IS a genuine hole (four
     * entities changed between the seed and the subscription) and catching up is right.
     *
     * @param deltas - the frame's entities, in whatever order the server sent them.
     * @sideEffect May call `reconcile()` (one HTTP catch-up), de-duped while in flight.
     */
    private detectGap(deltas: ReadonlyArray<TDelta>): void {
        this.detectGapAt(deltas.map((delta) => this.config.cursorOf(delta)));
    }

    /**
     * {@link detectGap} over raw positions, so a departure frame (BUG-1766) — which
     * carries numbers, not deltas — is gap-checked by the same rule.
     *
     * @param positions - the frame's positions in the topic's series.
     * @sideEffect May call `reconcile()` (one HTTP catch-up), de-duped while in flight.
     */
    private detectGapAt(positions: ReadonlyArray<number>): void {
        if (!this.isSequenced || positions.length === 0) return;
        let lowest = Infinity;
        for (const position of positions) {
            if (position < lowest) lowest = position;
        }
        if (lowest <= this.maxCursor + 1) return;
        if (this.gapReconcileInFlight) return;
        this.gapReconcileInFlight = true;
        void this.reconcile().finally(() => {
            this.gapReconcileInFlight = false;
        });
    }

    /**
     * Schedule ONE coalesced render notification (BUG-1407).
     *
     * The FOLD is already applied by every caller before this runs, so
     * `getEntities()` / `getConnection()` are correct the instant a delta lands —
     * this defers only the "tell React to re-render" half. That split is the
     * whole fix: consumers never read stale state, but a burst of deltas from one
     * server transaction surfaces as a single render holding the finished map
     * instead of N renders each holding a partial one.
     *
     * Why it matters beyond render count: a consumer that DERIVES order from the
     * folded state (the clerk console's Items catalog sorts by close time) had no
     * way to tell a half-applied burst from a real reordering, so an auction-wide
     * close-time change rendered as the list visibly rotating through every
     * intermediate permutation before settling.
     *
     * Already-pending is a no-op — this batches, it does NOT debounce. Restarting
     * the timer on each delta would starve a continuously-bidding auction, where
     * deltas never stop arriving.
     */
    private notify(): void {
        if (this.pendingNotify) return;
        this.pendingNotify = this.scheduleNotify(() => {
            this.pendingNotify = null;
            // Publish BEFORE notifying: a listener reads `getEntities()` and
            // `getConnection()` from inside its callback, so both promotions have
            // to be visible by then.
            this.publishPending();
            for (const listener of this.listeners) listener();
        });
    }

    /** Drop a scheduled-but-unflushed notify (teardown). */
    private cancelPendingNotify(): void {
        this.pendingNotify?.();
        this.pendingNotify = null;
        // The VALUE still has to be correct after teardown — only the RENDER is
        // dropped. Promote whatever the cancelled flush was holding so a later
        // `getEntities()` cannot read behind deltas the store already folded, and
        // a later `getConnection()` cannot read behind the `isSubscribed: false`
        // `stop()` just staged (BUG-1562).
        this.publishPending();
    }
}
